import { useEffect, useState } from "react";
import { Star, Quote, ChevronLeft, ChevronRight } from "lucide-react";
import { TESTIMONIALS } from "@/data/salonData";
import { Reveal } from "@/lib/useReveal";

export default function Testimonials() {
  const [index, setIndex] = useState(0);
  const total = TESTIMONIALS.length;

  useEffect(() => {
    const id = setInterval(() => setIndex((i) => (i + 1) % total), 6000);
    return () => clearInterval(id);
  }, [total]);

  const t = TESTIMONIALS[index];

  return (
    <section
      id="testimonials"
      data-testid="testimonials-section"
      className="relative py-20 md:py-28 bg-white overflow-hidden"
    >
      <div className="absolute inset-0 -z-10">
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-gradient-radial from-[#fff0f5] to-transparent rounded-full" />
      </div>

      <div className="max-w-5xl mx-auto px-5 md:px-8 text-center">
        <Reveal>
          <span className="heading-flourish">Love From Clients</span>
          <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl text-[#2a1b18] font-medium mt-4 leading-tight">
            Stories That Make Us <span className="text-gradient-rose italic">Blush</span>
          </h2>
        </Reveal>

        <Reveal delay={150}>
          <div className="mt-12 relative">
            <Quote
              size={60}
              className="absolute -top-4 left-1/2 -translate-x-1/2 text-[#f9d0da]"
              strokeWidth={1}
            />

            <div
              key={index}
              data-testid="testimonial-active"
              className="relative bg-gradient-to-b from-white to-[#fff5f8] border border-[#d98a99]/15 rounded-3xl p-8 md:p-12 shadow-[0_20px_50px_-20px_rgba(217,138,153,0.2)]"
            >
              <div className="flex justify-center gap-1 mb-5">
                {Array.from({ length: t.rating }).map((_, i) => (
                  <Star
                    key={i}
                    size={18}
                    fill="#d4af37"
                    className="text-[#d4af37]"
                  />
                ))}
              </div>
              <p className="font-display text-xl md:text-2xl lg:text-3xl italic text-[#2a1b18] leading-snug max-w-3xl mx-auto">
                "{t.text}"
              </p>
              <div className="mt-7">
                <div className="font-display text-xl text-[#b56477] font-semibold">
                  {t.name}
                </div>
                <div className="text-xs uppercase tracking-[0.2em] text-[#5c4642]/70 mt-1">
                  {t.role}
                </div>
              </div>
            </div>

            {/* Controls */}
            <div className="mt-6 flex items-center justify-center gap-3">
              <button
                data-testid="testimonial-prev"
                onClick={() => setIndex((i) => (i - 1 + total) % total)}
                className="w-10 h-10 rounded-full border border-[#d98a99]/30 text-[#b56477] hover:bg-[#fff0f5] flex items-center justify-center transition-colors"
                aria-label="Previous testimonial"
              >
                <ChevronLeft size={18} />
              </button>
              <div className="flex gap-1.5">
                {TESTIMONIALS.map((_, i) => (
                  <button
                    key={i}
                    data-testid={`testimonial-dot-${i}`}
                    onClick={() => setIndex(i)}
                    className={`h-1.5 rounded-full transition-all ${
                      i === index
                        ? "w-8 bg-[#b56477]"
                        : "w-1.5 bg-[#d98a99]/30"
                    }`}
                    aria-label={`Go to testimonial ${i + 1}`}
                  />
                ))}
              </div>
              <button
                data-testid="testimonial-next"
                onClick={() => setIndex((i) => (i + 1) % total)}
                className="w-10 h-10 rounded-full border border-[#d98a99]/30 text-[#b56477] hover:bg-[#fff0f5] flex items-center justify-center transition-colors"
                aria-label="Next testimonial"
              >
                <ChevronRight size={18} />
              </button>
            </div>
          </div>
        </Reveal>
      </div>
    </section>
  );
}
